import {
  ExistingExercisePayloadState,
  RegularExerciseHelper,
} from "./RegularExerciseHelper";
import {FormGroup} from "@angular/forms";
import {IPairedExerciseForm} from "../../../shared/interfaces/forms/exercises/IPairedExerciseForm";
import {EExerciseType} from "../../../shared/enums/EExerciseType";

export type PairedExerciseSide = "first" | "second";

export interface ExistingPairedExercisePayloadState {
  boardType?: number;
  isFavorite?: boolean;
  firstExercise?: ExistingExercisePayloadState;
  secondExercise?: ExistingExercisePayloadState;
}

export class PairedExerciseHelper {
  static isFormValid(formGroup: FormGroup<IPairedExerciseForm>): boolean {
    if (formGroup.controls.type.value === EExerciseType.Companion) {
      return false;
    }
    return (
      formGroup.controls.category.valid &&
      formGroup.controls.name.valid &&
      formGroup.controls.type.valid &&
      RegularExerciseHelper.isFormValid(
        formGroup.controls.firstExercise,
        formGroup.controls.type,
      ) &&
      RegularExerciseHelper.isFormValid(
        formGroup.controls.secondExercise,
        formGroup.controls.type,
      )
    );
  }

  static createPayload(
    formGroup: FormGroup<IPairedExerciseForm>,
    existingExercise?: ExistingPairedExercisePayloadState,
  ) {
    const value = {
      categoryId: formGroup.controls.category.value.id,
      name: formGroup.controls.name.value,
      type: formGroup.controls.type.value,
      boardType: existingExercise?.boardType ?? 0,
      isFavorite: existingExercise?.isFavorite ?? false,
      firstExercise: RegularExerciseHelper.createPayload(
        formGroup.controls.firstExercise,
        existingExercise?.firstExercise,
      ),
      secondExercise: RegularExerciseHelper.createPayload(
        formGroup.controls.secondExercise,
        existingExercise?.secondExercise,
      ),
    };
    return value;
  }

  static mirrorRequiredConfig(
    formGroup: FormGroup<IPairedExerciseForm>,
    sourceSide: PairedExerciseSide,
  ): void {
    const source =
      sourceSide === "first"
        ? formGroup.controls.firstExercise
        : formGroup.controls.secondExercise;
    const target =
      sourceSide === "first"
        ? formGroup.controls.secondExercise
        : formGroup.controls.firstExercise;
    const sourceConfig = source.controls.config;
    const targetConfig = target.controls.config;
    const type = formGroup.controls.type.value;
    const category = formGroup.controls.category.value;

    source.controls.type.setValue(type, {emitEvent: false});
    target.controls.type.setValue(type, {emitEvent: false});
    source.controls.category.setValue(category, {emitEvent: false});
    target.controls.category.setValue(category, {emitEvent: false});
    targetConfig.controls.difficulty.setValue(
      sourceConfig.controls.difficulty.value,
      {emitEvent: false},
    );

    if (type !== EExerciseType.Vertical) {
      targetConfig.controls.countdownForm.patchValue(
        sourceConfig.controls.countdownForm.getRawValue(),
        {emitEvent: false},
      );
    }

    switch (type) {
      case EExerciseType.Count:
        targetConfig.controls.countForm.patchValue(
          sourceConfig.controls.countForm.getRawValue(),
          {emitEvent: false},
        );
        break;
      case EExerciseType.React: {
        const react = sourceConfig.controls.reactForm.controls;
        targetConfig.controls.reactForm.patchValue(
          {
            delay: react.delay.value,
            timerPrompt: react.timerPrompt.value,
            neurocognitiveType: react.neurocognitiveType.value,
            stroopEffectType: react.stroopEffectType.value,
            liftTime: react.liftTime.value,
            footFireActivity: react.footFireActivity.value,
            reactTo: [...react.reactTo.value],
            dontReactTo: [...react.dontReactTo.value],
          },
          {emitEvent: false},
        );
        break;
      }
      case EExerciseType.Sequence:
        targetConfig.controls.sequenceForm.patchValue(
          {
            delay: sourceConfig.controls.sequenceForm.controls.delay.value,
            timerPrompt:
              sourceConfig.controls.sequenceForm.controls.timerPrompt.value,
          },
          {emitEvent: false},
        );
        break;
      case EExerciseType.Agility:
        targetConfig.controls.agilityForm.patchValue(
          {
            delay: sourceConfig.controls.agilityForm.controls.delay.value,
            timerPrompt:
              sourceConfig.controls.agilityForm.controls.timerPrompt.value,
          },
          {emitEvent: false},
        );
        break;
      case EExerciseType.Vertical:
        targetConfig.controls.verticalForm.patchValue(
          sourceConfig.controls.verticalForm.getRawValue(),
          {emitEvent: false},
        );
        break;
    }

    targetConfig.updateValueAndValidity({emitEvent: false});
  }

  static patchFormConfig(
    formGroup: FormGroup<IPairedExerciseForm>,
    value: any,
    sourceSide: PairedExerciseSide = "first",
  ): boolean {
    const defaultPairedConfig = value.pairedExerciseConfigs.find(
        (config) => config.id === value.defaultConfigId
    );
    if (!defaultPairedConfig) return false;

    const childValues = PairedExerciseHelper.pairedChildValues(
      value,
      defaultPairedConfig,
    );

    PairedExerciseHelper.patchInSourceOrder(sourceSide, (side) => {
      RegularExerciseHelper.patchFormConfig(
        side === "first"
          ? formGroup.controls.firstExercise
          : formGroup.controls.secondExercise,
        childValues[side],
      );
    });
    PairedExerciseHelper.mirrorRequiredConfig(formGroup, sourceSide);
    return true;
  }

  static patchFormValue(
    formGroup: FormGroup<IPairedExerciseForm>,
    value: any,
  ): boolean {
    const defaultPairedConfig = value.pairedExerciseConfigs.find(
      (config) => config.id === value.defaultConfigId
    );
    if (!defaultPairedConfig) return false;

    const childValues = PairedExerciseHelper.pairedChildValues(
      value,
      defaultPairedConfig,
    );

    formGroup.patchValue({
      category: {
        id: value.categoryId,
        name: null,
      },
      name: value.name,
      type: value.type,
    });

    let patched = true;
    PairedExerciseHelper.patchInSourceOrder("first", (side) => {
      patched =
        RegularExerciseHelper.patchFormValue(
          side === "first"
            ? formGroup.controls.firstExercise
            : formGroup.controls.secondExercise,
          childValues[side],
        ) && patched;
    });
    PairedExerciseHelper.mirrorRequiredConfig(formGroup, "first");
    return patched;
  }

  private static pairedChildValues(
    value: any,
    pairedConfig: any,
  ): Record<PairedExerciseSide, any> {
    const shared = {
      type: value.type,
      category: {
        id: value.categoryId,
        name: null,
      },
      categoryId: value.categoryId,
    };
    return {
      first: {
        ...value.firstExercise,
        ...shared,
        defaultConfigId: pairedConfig.firstConfigId,
      },
      second: {
        ...value.secondExercise,
        ...shared,
        defaultConfigId: pairedConfig.secondConfigId,
      },
    };
  }

  private static patchInSourceOrder(
    sourceSide: PairedExerciseSide,
    patch: (side: PairedExerciseSide) => void,
  ): void {
    const targetSide: PairedExerciseSide =
      sourceSide === "first" ? "second" : "first";
    patch(targetSide);
    patch(sourceSide);
  }
}
